import React from 'react';
import moment from 'moment';
import { logError } from 'utils/logger';
import { Box, Button, Stack } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { DateRangeWithPeriods, InputField, SelectField } from 'components/form';

import { REPORT_FILTERS } from '../../constants';
import { ReportsListingFilters } from '../../types';
import { VOApiFilterType } from '../../../vendorOpportunity';
import { useFilterOptions } from '../../../vendorOpportunity/list/api';

interface Props {
  onSubscribe: (filters: ReportsListingFilters) => void;
}

export const ReportFilters = ({ onSubscribe }: Props) => {
  const { options: salesOptions, loading: salesOptionsLoading } = useFilterOptions(VOApiFilterType.SALES_REP);
  const { options: locationOptions, loading: locationOptionsLoading } = useFilterOptions(VOApiFilterType.LOCATION);
  const {
    options: stageNameOptions,
    loading: stageOptionsLoading,
    refetch: refetchStageNameOptions,
  } = useFilterOptions(VOApiFilterType.BPF_STAGE_NAME);
  const { options: departmentOptions, loading: departmentOptionsLoading } = useFilterOptions(
    VOApiFilterType.BPF_DEPARTMENT
  );

  const { control, setValue, reset } = useForm<{ filters: ReportsListingFilters }>({
    defaultValues: { filters: REPORT_FILTERS },
  });

  const filters = useWatch({ control, name: 'filters' });

  const handleSelectRange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('filters.to', range.endDate);
    setValue('filters.from', range.startDate);

    onSubscribe({ ...filters, to: range.endDate, from: range.startDate });
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto', gap: '1rem' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <DateRangeWithPeriods
          control={control}
          nameTo="filters.to"
          nameFrom="filters.from"
          onRangeChange={handleSelectRange}
        />

        <Button
          onClick={() => {
            reset({ filters: REPORT_FILTERS });
            onSubscribe(REPORT_FILTERS);
            refetchStageNameOptions({ input: { filterType: VOApiFilterType.BPF_STAGE_NAME } }).catch(logError);
          }}
        >
          Reset
        </Button>
      </Stack>

      <Stack gap="0.5rem" direction="row">
        <InputField
          label="Search"
          control={control}
          name="filters.search"
          inputProps={{ variant: 'outlined' }}
          extraAction={{
            debounceTime: 300,
            cb: search => onSubscribe({ ...filters, search }),
          }}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Salesperson"
          options={salesOptions}
          name="filters.salesPersonId"
          isLoading={salesOptionsLoading}
          extraAction={nextValue => onSubscribe({ ...filters, salesPersonId: nextValue.toString() })}
        />

        <SelectField
          label="Location"
          control={control}
          variant="outlined"
          name="filters.locationId"
          options={locationOptions}
          isLoading={locationOptionsLoading}
          extraAction={nextValue => onSubscribe({ ...filters, locationId: nextValue.toString() })}
        />

        <SelectField
          label="Department"
          control={control}
          variant="outlined"
          options={departmentOptions}
          name="filters.departmentId"
          isLoading={departmentOptionsLoading}
          extraAction={nextValue => {
            setValue('filters.stageName', '');
            onSubscribe({ ...filters, departmentId: nextValue.toString(), stageName: '' });
            refetchStageNameOptions({
              input: {
                filterType: VOApiFilterType.BPF_STAGE_NAME,
                metaVariables: { departmentId: nextValue.toString() },
              },
            }).catch(logError);
          }}
        />

        <SelectField
          label="Stage"
          control={control}
          variant="outlined"
          name="filters.stageName"
          options={stageNameOptions}
          isLoading={stageOptionsLoading}
          extraAction={nextValue => onSubscribe({ ...filters, stageName: nextValue.toString() })}
        />
      </Stack>
    </Box>
  );
};
